'use client'

import { loadTossPayments } from '@tosspayments/tosspayments-sdk'

import type {
  PendingBillingSetup,
  BrandpaySession,
} from '@/features/veluvit/schemas/billing.schema'
import type { PaymentCheckout } from '@/features/veluvit/schemas/payment.schema'

type CheckoutOptions = {
  windowTarget?: 'iframe' | 'self'
  mode?: 'redirect' | 'promise'
  signal?: AbortSignal
  onOpen?: () => void
}

export async function openTossCheckout(
  checkout: PaymentCheckout,
  active: () => boolean,
  options: CheckoutOptions = {}
): Promise<unknown> {
  const sdk = await loadTossPayments(checkout.clientKey)
  if (!active() || options.signal?.aborted) return
  const request = {
    orderId: checkout.orderId,
    orderName: checkout.orderName,
    amount: { currency: 'KRW' as const, value: checkout.amount },
    customerName: checkout.customerName,
    customerEmail: checkout.customerEmail,
  }
  const redirect = options.mode !== 'promise'
  if (redirect && (!checkout.successUrl || !checkout.failUrl))
    throw new Error('결제 복귀 주소를 확인할 수 없어요.')
  const payload = {
    ...request,
    ...(redirect
      ? { successUrl: checkout.successUrl, failUrl: checkout.failUrl }
      : {}),
  }
  if (checkout.paymentOption.kind === 'brandpay') {
    options.onOpen?.()
    return sdk
      .brandpay({
        customerKey: checkout.customerKey,
        redirectUrl: checkout.brandpayRedirectUrl,
      })
      .requestPayment({ ...payload, methodId: checkout.methodId })
  }
  const payment = sdk.payment({ customerKey: checkout.customerKey })
  const destroy = () => {
    void payment.destroy().catch(() => {})
  }
  options.signal?.addEventListener('abort', destroy, { once: true })
  try {
    options.onOpen?.()
    const direct = {
      ...payload,
      windowTarget: options.windowTarget ?? 'self',
      customerMobilePhone: checkout.customerMobilePhone,
    }
    const { method, easyPay } = checkout.paymentOption
    if (method === 'TRANSFER')
      return await payment.requestPayment({ ...direct, method: 'TRANSFER' })
    else if (method === 'VIRTUAL_ACCOUNT')
      return await payment.requestPayment({
        ...direct,
        method: 'VIRTUAL_ACCOUNT',
        virtualAccount: checkout.virtualAccount,
      })
    else if (method === 'MOBILE_PHONE')
      return await payment.requestPayment({ ...direct, method: 'MOBILE_PHONE' })
    else
      return await payment.requestPayment({
        ...direct,
        method: 'CARD',
        ...(easyPay ? { card: { flowMode: 'DIRECT' as const, easyPay } } : {}),
      })
  } finally {
    options.signal?.removeEventListener('abort', destroy)
  }
}

export async function openTossBilling(
  setup: PendingBillingSetup,
  active: () => boolean
) {
  const sdk = await loadTossPayments(setup.clientKey)
  if (!active()) return false
  if (setup.option.method === 'BRANDPAY') {
    await sdk
      .brandpay({
        customerKey: setup.customerKey,
        redirectUrl: setup.brandpayRedirectUrl,
      })
      .requestBillingAuth()
    return active()
  }
  const payment = sdk.payment({ customerKey: setup.customerKey })
  const request = {
    method: 'CARD' as const,
    successUrl: setup.successUrl,
    failUrl: setup.failUrl,
    windowTarget: 'self' as const,
  }
  if (setup.option.method === 'CARD') await payment.requestBillingAuth(request)
  else
    await payment.requestBillingAuth({
      ...request,
      flowMode: 'DIRECT',
      easyPay: setup.option.method,
    })
  return false
}

export async function openBrandpayCardRegistration(
  session: BrandpaySession,
  active: () => boolean,
  options: CheckoutOptions = {}
) {
  const sdk = await loadTossPayments(session.clientKey)
  if (!active() || options.signal?.aborted) return
  options.onOpen?.()
  await sdk
    .brandpay({
      customerKey: session.customerKey,
      redirectUrl: session.redirectUrl,
    })
    .addPaymentMethod()
}

export async function openBrandpaySettings(
  session: BrandpaySession,
  active: () => boolean,
  options: CheckoutOptions = {}
) {
  const sdk = await loadTossPayments(session.clientKey)
  if (!active() || options.signal?.aborted) return
  options.onOpen?.()
  await sdk
    .brandpay({
      customerKey: session.customerKey,
      redirectUrl: session.redirectUrl,
    })
    .openSettings()
}
